import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Settings, Hourglass } from 'lucide-react';
import { Tabs, type TabDef } from '@/components/ui/Tabs';
import { KpiConsolidesContent } from '@/features/kpi-consolides/pages/KpiConsolidesPage';
import { DeBelemContent } from '@/features/de-belem/components/DeBelemContent';
import { Button } from '@/components/ui/Button';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/StatusBadge';
import { Tooltip as UiTooltip } from '@/components/ui/Tooltip';
import { Banner } from '@/components/ui/Banner';
import { Skeleton } from '@/components/ui/Skeleton';
import { Select } from '@/components/ui/Input';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  AXIS_STYLE,
  CHART_COLORS,
  ChartTooltip,
  formatEuroCompact,
  bucketToIsoRange,
  disambiguateBucketLabels,
  GRID_STYLE,
} from '@/components/ui/chart';
import { useAuthStore } from '@/stores/auth';
import { useDebouncedRealtimeRefresh, REALTIME_EVENTS } from '@/hooks/useRealtimeEvent';
import {
  computeGranularity,
  presetPeriod,
  type Granularity,
  type KpiFilters,
  type PeriodPreset,
} from '@/services/kpi.api';
import {
  DateRangeBadges,
  DateRangeInputs,
  type DateRange,
  type DateRangePreset,
} from '@/components/ui/DateRangePicker';
import {
  kpiDirectionApi,
  LOSS_REASON_LABELS,
  type CashInEvolutionResponse,
  type CompanyConversionResponse,
  type ContractTypeConversionResponse,
  type ForecastResponse,
  type InquiriesStatsResponse,
  type LossAnalysisResponse,
  type PeriodComparisonResponse,
  type RevenueByOriginResponse,
  type SignedRevenueEvolutionResponse,
  type TopApporteursResponse,
  type UserPerformanceResponse,
  type UserPerformanceRow,
} from '@/services/kpiDirection.api';
import { ORIGIN_VISUAL, type OpportunityOrigin } from '@/services/opportunities.api';
import { displayName as apporteurDisplayName } from '@shared/apporteur';
import {
  objectivesApi,
  OBJECTIVE_PERIOD_LABELS,
  OBJECTIVE_TYPE_LABELS,
  type ObjectiveItem,
  type ObjectivePeriod,
  type ObjectiveProgress,
  type ObjectiveType,
} from '@/services/objectives.api';
import { usersApi, type TeamMember } from '@/services/users.api';
import { cn } from '@/lib/utils';
import { getAccentHex } from '@/lib/accent';

type PresetKey = Extract<PeriodPreset, 'month' | 'quarter' | 'year' | 'last12months'>;

// Helpers UTC.
function utcStart(y: number, m: number, d: number): string {
  return new Date(Date.UTC(y, m, d)).toISOString();
}
function utcEndOfDay(y: number, m: number, d: number): string {
  return new Date(Date.UTC(y, m, d, 23, 59, 59, 999)).toISOString();
}

const DIRECTION_PRESETS: ReadonlyArray<DateRangePreset<PresetKey>> = [
  {
    key: 'month',
    label: 'Ce mois',
    range: () => {
      const n = new Date();
      return {
        from: utcStart(n.getUTCFullYear(), n.getUTCMonth(), 1),
        to: utcEndOfDay(n.getUTCFullYear(), n.getUTCMonth() + 1, 0),
      };
    },
  },
  {
    key: 'quarter',
    label: 'Ce trimestre',
    range: () => {
      const n = new Date();
      const q = Math.floor(n.getUTCMonth() / 3);
      return {
        from: utcStart(n.getUTCFullYear(), q * 3, 1),
        to: utcEndOfDay(n.getUTCFullYear(), q * 3 + 3, 0),
      };
    },
  },
  {
    key: 'year',
    label: 'Cette année',
    // Décision produit 2026-07-02 (demande client) : « Cette année » = fenêtre
    // « année d'effet » décalée — du 02 janvier N au 01 janvier N+1 (inclus).
    // Aligné avec date-presets.ts (Dashboard/Pipeline) + la card Trésorerie
    // (`presetPeriod('year')`). Remplace l'ancien year-to-date (01/01 → aujourd'hui).
    // S'applique à tous les onglets (filtre partagé au niveau du shell).
    range: () => {
      const y = new Date().getUTCFullYear();
      return {
        from: utcStart(y, 0, 2),
        to: utcEndOfDay(y + 1, 0, 1),
      };
    },
  },
  {
    key: 'last12months',
    label: '12 derniers mois',
    range: () => {
      const n = new Date();
      const from = new Date(Date.UTC(n.getUTCFullYear() - 1, n.getUTCMonth() + 1, 1));
      return {
        from: from.toISOString(),
        to: utcEndOfDay(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()),
      };
    },
  },
];

function initialDirectionRange(): DateRange {
  return DIRECTION_PRESETS.find((p) => p.key === 'year')!.range();
}

// ---------------------------------------------------------------------------
// Shell tabs : Groupe LBC · Adélie · De Belem · KPI consolidés (agrégé).
// Le filtre temporel (DateRangePicker + presets) reste au niveau du shell et
// s'applique à tous les tabs. Le tab actif est persisté dans `?tab=`.
// ---------------------------------------------------------------------------

type DirectionTab = 'groupe-lbc' | 'adelie' | 'de-belem' | 'kpi-consolides';

const DIRECTION_TABS: ReadonlyArray<TabDef<DirectionTab>> = [
  { key: 'groupe-lbc', label: 'Groupe LBC' },
  { key: 'adelie', label: 'Adélie' },
  { key: 'de-belem', label: 'De Belem' },
  { key: 'kpi-consolides', label: 'KPI consolidés' },
];

function isDirectionTab(v: string | null): v is DirectionTab {
  return v === 'groupe-lbc' || v === 'adelie' || v === 'de-belem' || v === 'kpi-consolides';
}

/** Convertit une date ISO complète (`YYYY-MM-DDTHH:mm:ss.sssZ`) en
 *  `YYYY-MM-DD` attendu par les API métier. */
function isoToDateOnly(iso: string): string {
  return iso.slice(0, 10);
}

// Mapping horizon de prévision → liste de stages actives dont l'expected
// close (cf STAGE_EXPECTED_CLOSE_DAYS côté backend) est ≤ horizon. Doit
// rester aligné avec le backend (`kpi.service.ts` § getRevenueForecast).
// Refonte 2026-05-21 : nouveau pipeline LBC à 7 stages non-terminaux.
const FORECAST_HORIZON_STAGES: Record<number, string[]> = {
  30: ['quote_sent'],
  60: ['quote_sent', 'awaiting_quote_supplier'],
  90: ['quote_sent', 'awaiting_quote_supplier', 'awaiting_client_docs'],
  180: [
    'quote_sent',
    'awaiting_quote_supplier',
    'awaiting_client_docs',
    'meeting_done',
    'meeting_booked',
    'contacted',
  ],
  365: [
    'quote_sent',
    'awaiting_quote_supplier',
    'awaiting_client_docs',
    'meeting_done',
    'meeting_booked',
    'contacted',
    'suspect',
  ],
};

/** Ouvre la page Pipeline en vue liste, dans un nouvel onglet, avec les
 *  filtres passés en query string. Utilisé par les onClick des graphes. */
function openInPipeline(params: Record<string, string | undefined>): void {
  const qs = new URLSearchParams({ view: 'list' });
  for (const [k, v] of Object.entries(params)) {
    if (v) qs.set(k, v);
  }
  window.open(`/pipeline?${qs.toString()}`, '_blank', 'noopener,noreferrer');
}

export function KpiDirectionPage() {
  const user = useAuthStore((s) => s.user);
  const isDirection =
    user?.role === 'admin' || user?.role === 'director' || user?.role === 'executive';

  const [range, setRange] = useState<DateRange>(initialDirectionRange);
  const [activePreset, setActivePreset] = useState<PresetKey | null>('year');

  // Persistance du tab actif dans l'URL → reload / share conserve le tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const currentTab: DirectionTab = isDirectionTab(tabParam) ? tabParam : 'groupe-lbc';

  function handleTabChange(next: DirectionTab): void {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  }

  if (!isDirection) return <Navigate to="/" replace />;

  return (
    <AppLayout>
      <PageHeader
        title="KPIs direction"
        subtitle="Performance commerciale, prévisions CA, analyse motifs de perte."
        actions={
          <div className="flex flex-col items-end gap-1.5">
            <DateRangeInputs
              value={range}
              onChange={(next, key) => {
                setRange(next);
                setActivePreset(key);
              }}
            />
            <DateRangeBadges
              activePresetKey={activePreset}
              presets={DIRECTION_PRESETS}
              onChange={(next, key) => {
                setRange(next);
                setActivePreset(key);
              }}
              align="end"
            />
          </div>
        }
      />

      <Tabs tabs={DIRECTION_TABS} current={currentTab} onChange={handleTabChange} />

      {currentTab === 'groupe-lbc' && <GroupeLbcTab range={range} activePreset={activePreset} />}
      {currentTab === 'adelie' && (
        <KpiConsolidesContent
          dateFrom={isoToDateOnly(range.from)}
          dateTo={isoToDateOnly(range.to)}
        />
      )}
      {currentTab === 'de-belem' && (
        <DeBelemContent dateFrom={isoToDateOnly(range.from)} dateTo={isoToDateOnly(range.to)} />
      )}
      {currentTab === 'kpi-consolides' && (
        <WorkInProgressTab
          label="KPI consolidés (agrégé)"
          description="Vue agrégée Groupe LBC + Adélie + De Belem — en attente de l'API De Belem."
        />
      )}
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Tab « Groupe LBC » : le contenu historique de la page — 7 agrégats KPI
// direction + objectifs. Extrait du shell pour que le fetch ne se déclenche
// que quand ce tab est visible.
// ---------------------------------------------------------------------------

function GroupeLbcTab({
  range,
  activePreset,
}: {
  range: DateRange;
  activePreset: PresetKey | null;
}) {
  const token = useAuthStore((s) => s.accessToken)!;

  const [forecast, setForecast] = useState<ForecastResponse | null>(null);
  const [perf, setPerf] = useState<UserPerformanceResponse | null>(null);
  const [loss, setLoss] = useState<LossAnalysisResponse | null>(null);
  const [companyConv, setCompanyConv] = useState<CompanyConversionResponse | null>(null);
  const [typeConv, setTypeConv] = useState<ContractTypeConversionResponse | null>(null);
  const [periodCmp, setPeriodCmp] = useState<PeriodComparisonResponse | null>(null);
  const [signedEvo, setSignedEvo] = useState<SignedRevenueEvolutionResponse | null>(null);
  const [topApp, setTopApp] = useState<TopApporteursResponse | null>(null);
  const [originBreak, setOriginBreak] = useState<RevenueByOriginResponse | null>(null);
  const [inquiriesStats, setInquiriesStats] = useState<InquiriesStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const granularity: Granularity = computeGranularity(range.from, range.to);
  const filters: KpiFilters = {
    period_start: range.from,
    period_end: range.to,
    granularity,
  };
  const period = { period_start: range.from, period_end: range.to };

  async function load() {
    setError(null);
    try {
      const [f, u, l, c, t, p, s, ta, ob, ist] = await Promise.all([
        kpiDirectionApi.forecast(token, filters),
        kpiDirectionApi.userPerformance(token, filters),
        kpiDirectionApi.lossAnalysis(token, filters),
        kpiDirectionApi.companyConversion(token, filters),
        kpiDirectionApi.contractTypeConversion(token, filters),
        kpiDirectionApi.periodComparison(token, filters),
        kpiDirectionApi.signedRevenueEvolution(token, filters),
        kpiDirectionApi.topApporteurs(token, filters),
        kpiDirectionApi.revenueByOrigin(token, filters),
        kpiDirectionApi.inquiriesStats(token, filters),
      ]);
      setForecast(f);
      setPerf(u);
      setLoss(l);
      setCompanyConv(c);
      setTypeConv(t);
      setPeriodCmp(p);
      setSignedEvo(s);
      setTopApp(ta);
      setOriginBreak(ob);
      setInquiriesStats(ist);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  // Realtime : refresh debouncé 2s pour éviter le spam sur 7 queries
  // d'agrégation (forecast/perf/loss/conversions/period/signed) en cas
  // de burst de mutations.
  useDebouncedRealtimeRefresh(
    [
      REALTIME_EVENTS.OPPORTUNITY_UPDATED,
      REALTIME_EVENTS.PROSPECT_UPDATED,
      REALTIME_EVENTS.TASK_MUTATED,
    ],
    () => {
      void load();
    },
    2000,
  );

  return (
    <>
      {error && <Banner tone="danger">{error}</Banner>}
      {!perf && !error && (
        <div className="space-y-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-60" />
        </div>
      )}

      {perf && forecast && loss && companyConv && typeConv && periodCmp && signedEvo && topApp && (
        <>
          {/* 1. Cartes analytics de comparaison (4 mini-cards) */}
          <PeriodComparisonCard data={periodCmp} period={period} />

          {/* 2. Évolution CA signé (large) + Prévisions CA pondéré (étroit).
              Ratio 3:2 - cohérent avec la convention Dashboard "line large +
              card résumé à droite" pour les graphes temporels. */}
          <div className="my-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <SignedRevenueCard data={signedEvo} granularity={granularity} />
            </div>
            <div className="lg:col-span-2">
              <ForecastCard data={forecast} />
            </div>
          </div>

          {/* 3. Trésorerie (large) + Top apporteurs (étroit). Ratio 3:2
                 sur grid-cols-5 - aligné avec la row 2 (Signed Evo /
                 Forecast) pour que Forecast et Top apporteurs aient la
                 même largeur visuelle. La row grid étire les deux cards à
                 la même hauteur (la plus haute) ; le chart Trésorerie est
                 flex-1 (min 260px) → il épouse la hauteur de la table Top
                 apporteurs (variable selon le nombre d'apporteurs). */}
          <div className="my-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <CashInEvolutionCard token={token} mainPreset={activePreset ?? 'year'} />
            </div>
            <div className="lg:col-span-2">
              <TopApporteursCard data={topApp} />
            </div>
          </div>

          {/* 4. Conversions compagnie + type de contrat (2 colonnes
                 égales depuis 2026-04-27 - largeur libérée par le déplacement
                 de Top apporteurs sur la row Trésorerie). */}
          <div className="my-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <CompanyConversionCard data={companyConv} period={period} />
            <TypeConversionCard data={typeConv} period={period} />
          </div>

          {/* 4.ter Stats interrogations (compagnies sollicitées) — demande
                    LBC 2026-05-22. Mini-card pleine largeur avec 3 grands
                    chiffres : moyenne / taux transfo / taux refus. */}
          {inquiriesStats && inquiriesStats.inquiriesTotal > 0 && (
            <div className="my-6">
              <InquiriesStatsCard data={inquiriesStats} />
            </div>
          )}

          {/* 4.bis Répartition par origine (canal d'acquisition) — demande
                   client 2026-05-22. Donut à gauche (2/5), tableau breakdown
                   à droite (3/5) avec drill-down par ligne. */}
          {originBreak && originBreak.rows.length > 0 && (
            <div className="my-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
              <div className="lg:col-span-2">
                <RevenueByOriginDonut data={originBreak} />
              </div>
              <div className="lg:col-span-3">
                <RevenueByOriginTable data={originBreak} period={period} />
              </div>
            </div>
          )}

          {/* 5. Classement commerciaux - pleine largeur. */}
          <LeaderboardCard data={perf} />

          {/* 6. Objectifs commerciaux - pleine largeur. */}
          <ObjectivesSection />

          {/* 7. Évolution des pertes (large) + Motifs de perte pie (étroit).
              Même ratio 3:2 que ligne 2 pour cohérence visuelle. */}
          <div className="my-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <LossEvolutionCard data={loss} granularity={granularity} />
            </div>
            <div className="lg:col-span-2">
              <LossPieCard data={loss} period={period} />
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Placeholder réutilisé pour les tabs dont l'API source n'est pas encore
// branchée (De Belem V1, KPI consolidés agrégé V2).
// ---------------------------------------------------------------------------

function WorkInProgressTab({ label, description }: { label: string; description?: string }) {
  return (
    <Card>
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <Hourglass className="h-7 w-7 text-text-muted" />
        <CardTitle>{label}</CardTitle>
        <CardDescription>
          {description ??
            'Module en cours de développement — disponible dans une prochaine version.'}
        </CardDescription>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Objectifs commerciaux - vue direction avec filtres
// ---------------------------------------------------------------------------

function ObjectivesSection() {
  const token = useAuthStore((s) => s.accessToken)!;
  const navigate = useNavigate();
  const [users, setUsers] = useState<TeamMember[]>([]);
  const [items, setItems] = useState<Array<{ obj: ObjectiveItem; progress: ObjectiveProgress }>>(
    [],
  );
  const [filterUserId, setFilterUserId] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterPeriod, setFilterPeriod] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // Charge la liste des collaborateurs (pour le Select) une seule fois.
  useEffect(() => {
    let cancelled = false;
    usersApi
      .list(token, { activeOnly: true })
      .then((u) => {
        if (!cancelled) setUsers(u);
      })
      .catch(() => {
        /* silencieux */
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Charge les objectifs actifs + leur progression.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list = await objectivesApi.list(token, { active_only: true });
        const withProgress = await Promise.all(
          list.map(async (o) => ({ obj: o, progress: await objectivesApi.progress(token, o.id) })),
        );
        if (!cancelled) setItems(withProgress);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const filtered = useMemo(
    () =>
      items.filter(({ obj }) => {
        if (filterUserId && obj.user?.id !== filterUserId) return false;
        if (filterType && obj.type !== filterType) return false;
        if (filterPeriod && obj.periodType !== filterPeriod) return false;
        return true;
      }),
    [items, filterUserId, filterType, filterPeriod],
  );

  return (
    <section className="my-6">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Objectifs commerciaux</CardTitle>
            <CardDescription>
              Suivi des objectifs actifs par collaborateur, type et période
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Settings className="h-3.5 w-3.5" />}
              onClick={() => navigate('/config?tab=objectives')}
            >
              Gérer les objectifs
            </Button>
            <Select
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              className="w-auto"
              aria-label="Filtrer par collaborateur"
            >
              <option value="">Tous collaborateurs</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName}
                </option>
              ))}
            </Select>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-auto"
              aria-label="Filtrer par type"
            >
              <option value="">Tous types</option>
              {(Object.keys(OBJECTIVE_TYPE_LABELS) as ObjectiveType[]).map((t) => (
                <option key={t} value={t}>
                  {OBJECTIVE_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
            <Select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              className="w-auto"
              aria-label="Filtrer par période"
            >
              <option value="">Toutes périodes</option>
              {(Object.keys(OBJECTIVE_PERIOD_LABELS) as ObjectivePeriod[]).map((p) => (
                <option key={p} value={p}>
                  {OBJECTIVE_PERIOD_LABELS[p]}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="mt-4">
          {loading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              title="Aucun objectif"
              description="Aucun objectif actif ne correspond aux filtres en cours."
            />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map(({ obj, progress }) => {
                const ownerLabel = obj.user
                  ? `${obj.user.firstName} ${obj.user.lastName}`
                  : obj.teamId
                    ? 'Équipe'
                    : 'Global';
                return (
                  <div key={obj.id} className="rounded-md border border-border-subtle bg-white p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-brand">
                          {OBJECTIVE_TYPE_LABELS[obj.type]}
                        </div>
                        <div className="mt-0.5 truncate text-[11px] text-text-muted">
                          {ownerLabel} · {OBJECTIVE_PERIOD_LABELS[obj.periodType].toLowerCase()}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-[10px] uppercase tracking-wider text-text-muted">
                          Cible
                        </div>
                        <div className="text-sm font-semibold text-brand">
                          {progress.target.toLocaleString('fr-FR')}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all',
                          progress.progressPct >= 100
                            ? 'bg-success-600'
                            : progress.progressPct >= 50
                              ? 'bg-accent'
                              : 'bg-warning-500',
                        )}
                        style={{ width: `${Math.min(progress.progressPct, 100)}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px]">
                      <span className="text-text-muted">
                        {progress.achieved.toLocaleString('fr-FR')}
                      </span>
                      <span className="font-semibold text-brand">{progress.progressPct} %</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Comparatif périodes
// ---------------------------------------------------------------------------

/** Helper local pour les sous-composantes (Commissions / Frais+hono) qui
 *  n'ont pas leur propre `delta.*` côté serveur — calcul identique à
 *  `percentDelta` côté backend. Retourne null pour signaler "nouveau"
 *  quand la période N-1 était à zéro et la courante non. */
function percentDeltaLocal(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return Math.round(((current - previous) / previous) * 100);
}

function PeriodComparisonCard({
  data,
  period,
}: {
  data: PeriodComparisonResponse;
  period: { period_start: string; period_end: string };
}) {
  // Filtres temporels passés au pipeline pour respecter la période
  // sélectionnée. Les "signées" filtrent sur signed_at, les "créées" sur
  // created_at - sémantique alignée avec ce que la card affiche.
  const signedPeriodParams: Record<string, string> = {
    stage: 'won',
    signed_after: period.period_start,
    signed_before: period.period_end,
  };
  const createdPeriodParams: Record<string, string> = {
    created_after: period.period_start,
    created_before: period.period_end,
  };

  // Décision produit 2026-04-30 : la 1ʳᵉ ligne du Comparatif présente
  // 4 cards centrées sur les opps SIGNÉES (cohorte signedAt ∈ période) :
  //   1. Opportunités signées (count)
  //   2. Commissions    = SUM commission_amount
  //   3. Frais + honoraires = SUM fees + honoraires
  //   4. Rétrocommissions = SUM (com×rate_com + fees×rate_fees + hono×rate_hono) / 100
  // Les 3 cards financières partagent le même pivot (signedAt ∈ période) →
  // drill-down identique vers Pipeline filtré stage=won + signed_at∈période.
  // `createdPeriodParams` retiré (anciennement utilisé par "Opportunités créées").
  void createdPeriodParams;

  const metrics: Array<{
    label: string;
    cur: number;
    prev: number;
    delta: number | null;
    format: (v: number) => string;
    onClick?: () => void;
  }> = [
    {
      label: 'Opportunités signées',
      cur: data.current.signedCount,
      prev: data.previous.signedCount,
      delta: data.delta.signedCount,
      format: (v) => v.toLocaleString('fr-FR'),
      onClick: () => openInPipeline(signedPeriodParams),
    },
    {
      label: 'Commissions',
      cur: data.current.revenueSignedCommission,
      prev: data.previous.revenueSignedCommission,
      // Pas de `delta.revenueSignedCommission` exposé serveur — calcul local.
      delta: percentDeltaLocal(
        data.current.revenueSignedCommission,
        data.previous.revenueSignedCommission,
      ),
      format: (v) => formatEuroCompact(v),
      onClick: () => openInPipeline(signedPeriodParams),
    },
    {
      label: 'Frais et honoraires',
      cur: data.current.revenueSignedFeesAndHonoraires,
      prev: data.previous.revenueSignedFeesAndHonoraires,
      delta: percentDeltaLocal(
        data.current.revenueSignedFeesAndHonoraires,
        data.previous.revenueSignedFeesAndHonoraires,
      ),
      format: (v) => formatEuroCompact(v),
      onClick: () => openInPipeline(signedPeriodParams),
    },
    {
      label: 'Rétrocommissions',
      cur: data.current.retrocomsSigned,
      prev: data.previous.retrocomsSigned,
      delta: data.delta.retrocomsSigned,
      format: (v) => formatEuroCompact(v),
      onClick: () => openInPipeline(signedPeriodParams),
    },
  ];
  return (
    <Card>
      <CardTitle>Comparatif périodes</CardTitle>
      <CardDescription>Période actuelle vs précédente de même durée</CardDescription>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((m) => {
          const direction =
            m.delta === null ? 'new' : m.delta === 0 ? 'neutral' : m.delta > 0 ? 'up' : 'down';
          const badgeStyle: Record<typeof direction, string> = {
            up: 'bg-success-50 border-success-100 text-success-600',
            down: 'bg-danger-50 border-danger-100 text-danger-600',
            neutral: 'bg-gray-50 border-gray-200 text-gray-600',
            new: 'bg-gray-50 border-gray-200 text-gray-600',
          };
          return (
            <div key={m.label} className="rounded-md border border-border-subtle p-3">
              <div className="text-[11px] uppercase tracking-wider text-text-muted">{m.label}</div>
              {m.onClick ? (
                <button
                  type="button"
                  onClick={m.onClick}
                  className="mt-1 text-left text-xl font-semibold text-brand transition-colors hover:text-accent focus-visible:text-accent"
                >
                  {m.format(m.cur)}
                </button>
              ) : (
                <div className="mt-1 text-xl font-semibold text-brand">{m.format(m.cur)}</div>
              )}
              <div className="mt-2 flex items-center gap-1.5">
                <span
                  className={cn(
                    'inline-flex items-center rounded-full border px-1.5 py-0.5 text-[11px] font-semibold',
                    badgeStyle[direction],
                  )}
                >
                  {direction === 'new'
                    ? 'new'
                    : direction === 'neutral'
                      ? '0 %'
                      : `${m.delta! > 0 ? '+' : ''}${m.delta} %`}
                </span>
                <span className="text-[11px] text-text-muted">vs {m.format(m.prev)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Évolution CA signé - area chart avec N-1 (même design que dashboard)
// ---------------------------------------------------------------------------

/** Mode d'affichage du chart Évolution CA — cf décision Q A 2026-04-30.
 *  Mêmes valeurs que côté DashboardPage (helper local pour ne pas créer
 *  une primitive UI partagée tant qu'il n'y a que 2 instances). */
type SignedRevenueMode = 'total' | 'commission' | 'fees_hono';

function SignedRevenueCard({
  data,
  granularity,
}: {
  data: SignedRevenueEvolutionResponse;
  granularity: Granularity;
}) {
  const accent = getAccentHex();
  const [mode, setMode] = useState<SignedRevenueMode>('total');
  // Cumulatif : à chaque bucket on affiche la somme depuis le début de la
  // période. Permet de visualiser la trajectoire vs le rythme de l'an passé.
  // Bascule 2026-04-30 (Q A + Q7) :
  //   - mode 'total'      : cumul Total CA signé (com + frais + hono)
  //   - mode 'commission' : cumul commission seule
  //   - mode 'fees_hono'  : cumul frais + honoraires uniquement
  const rows = useMemo(() => {
    let curTotal = 0;
    let curCom = 0;
    let curFH = 0;
    let prevTotal = 0;
    let prevCom = 0;
    let prevFH = 0;
    // Labels désambiguïsés (année suffixée sur les buckets en collision) —
    // évite le tooltip/drill-down faux sur les fenêtres 13 mois (deux « janv. »).
    const labels = disambiguateBucketLabels(
      data.series.map((d) => d.month),
      granularity,
    );
    return data.series.map((d, i) => {
      curTotal += d.current;
      curCom += d.currentCommission;
      curFH += d.currentFeesAndHonoraires;
      prevTotal += d.previousYear;
      prevCom += d.previousYearCommission;
      prevFH += d.previousYearFeesAndHonoraires;
      const current = mode === 'commission' ? curCom : mode === 'fees_hono' ? curFH : curTotal;
      const previous = mode === 'commission' ? prevCom : mode === 'fees_hono' ? prevFH : prevTotal;
      return {
        month: labels[i],
        monthIso: d.month,
        current: Math.round(current),
        previous: Math.round(previous),
        currentCommission: Math.round(curCom),
        currentFeesAndHonoraires: Math.round(curFH),
      };
    });
  }, [data, granularity, mode]);

  function handleChartClick(state: unknown): void {
    // Click → opps signées dans CE bucket (et non cumulé jusqu'à ce bucket).
    // Sémantique "le bucket cliqué" alignée avec l'attente intuitive.
    const s = state as { activeLabel?: string } | null;
    const label = s?.activeLabel;
    if (!label) return;
    const row = rows.find((r) => r.month === label);
    if (!row) return;
    const range = bucketToIsoRange(row.monthIso, granularity);
    if (!range) return;
    openInPipeline({
      stage: 'won',
      signed_after: range.start,
      signed_before: range.end,
    });
  }
  const totals = useMemo(() => {
    // En cumulatif, le total = la dernière valeur de la série.
    const last = rows[rows.length - 1];
    const cur = last?.current ?? 0;
    const prev = last?.previous ?? 0;
    const deltaPct = prev === 0 ? null : Math.round(((cur - prev) / prev) * 100);
    return { cur, prev, deltaPct };
  }, [rows]);
  const allEmpty = totals.cur === 0 && totals.prev === 0;
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <CardTitle>Évolution CA signé (cumulé)</CardTitle>
          <CardDescription>CA HT signé cumulé mois après mois · comparé à N-1</CardDescription>
        </div>
        {/* Bloc droit du header (Stripe-like) : toggle compact + delta vs N-1
            empilés. Le toggle ne prend QUE la largeur de ses 3 boutons. */}
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <div className="inline-flex rounded-md border border-border-subtle bg-gray-50 p-[2px] text-[11px]">
            {(
              [
                { value: 'total', label: 'Total', title: 'CA total signé (com + frais + hono)' },
                { value: 'commission', label: 'Com.', title: 'Commissions seules' },
                { value: 'fees_hono', label: 'F+H', title: 'Frais + honoraires uniquement' },
              ] as Array<{ value: SignedRevenueMode; label: string; title: string }>
            ).map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setMode(o.value)}
                title={o.title}
                className={cn(
                  'rounded-[5px] px-2 py-0.5 font-medium leading-tight transition-colors',
                  mode === o.value
                    ? 'bg-white text-brand shadow-sm'
                    : 'text-text-muted hover:text-brand',
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
          {totals.deltaPct !== null && (
            <span
              className={cn(
                'text-xs font-medium',
                totals.deltaPct > 0
                  ? 'text-success-600'
                  : totals.deltaPct < 0
                    ? 'text-danger-600'
                    : 'text-text-muted',
              )}
            >
              {totals.deltaPct > 0 ? '+' : ''}
              {totals.deltaPct} % vs N-1
            </span>
          )}
        </div>
      </div>
      <div className="mt-4 h-[260px] w-full">
        {allEmpty ? (
          <EmptyState
            title="Aucun CA signé"
            description="Aucune opportunité signée sur la période sélectionnée."
          />
        ) : (
          <ResponsiveContainer>
            <AreaChart
              data={rows}
              margin={{ left: 0, right: 8, top: 4, bottom: 0 }}
              onClick={handleChartClick}
              style={{ cursor: 'pointer' }}
            >
              <defs>
                <linearGradient id="signedRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="month" {...AXIS_STYLE} />
              <YAxis
                {...AXIS_STYLE}
                tickFormatter={(v) => formatEuroCompact(Number(v))}
                width={56}
              />
              <Tooltip
                cursor={{ stroke: '#E5E7EB', strokeWidth: 1 }}
                content={<ChartTooltip formatValue={formatEuroCompact} />}
              />
              <Legend
                verticalAlign="top"
                align="right"
                height={24}
                iconType="circle"
                wrapperStyle={{ fontSize: 11, color: '#6B7280' }}
              />
              <Area
                type="monotone"
                name="N-1"
                dataKey="previous"
                stroke="#9CA3AF"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                fill="transparent"
                dot={false}
                activeDot={{ r: 3, fill: '#9CA3AF' }}
              />
              {/* Mode 'total' (Q7) : stacked area com + F+H. Sinon : single. */}
              {mode === 'total' ? (
                <>
                  <Area
                    type="monotone"
                    name="Commissions"
                    dataKey="currentCommission"
                    stackId="signed"
                    stroke={accent}
                    strokeWidth={2}
                    fill="url(#signedRevenueGradient)"
                    dot={false}
                    activeDot={{ r: 4, fill: accent, strokeWidth: 2, stroke: '#FFFFFF' }}
                  />
                  <Area
                    type="monotone"
                    name="Frais + honoraires"
                    dataKey="currentFeesAndHonoraires"
                    stackId="signed"
                    stroke="#A78BFA"
                    strokeWidth={2}
                    fill="#EDE9FE"
                    fillOpacity={0.6}
                    dot={false}
                    activeDot={{ r: 4, fill: '#A78BFA', strokeWidth: 2, stroke: '#FFFFFF' }}
                  />
                </>
              ) : (
                <Area
                  type="monotone"
                  name={mode === 'commission' ? 'Commissions' : 'Frais + honoraires'}
                  dataKey="current"
                  stroke={accent}
                  strokeWidth={2}
                  fill="url(#signedRevenueGradient)"
                  dot={false}
                  activeDot={{ r: 4, fill: accent, strokeWidth: 2, stroke: '#FFFFFF' }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Trésorerie : évolution des encaissements (basés sur la date de prise
// d'effet du contrat). Sa propre période locale, indépendante du filtre
// principal de la page → la direction peut consulter ce qui doit rentrer
// les 6/12 prochains mois sans changer le filtre principal.
// ---------------------------------------------------------------------------

const CASH_IN_PRESETS: Array<{ key: PeriodPreset; label: string }> = [
  { key: 'last12months', label: '12 derniers mois' },
  { key: 'year', label: 'Cette année' },
  { key: 'next6months', label: '6 prochains mois' },
  { key: 'next12months', label: '12 prochains mois' },
  { key: 'spanLastNext12months', label: '12 mois passés + 12 à venir' },
];

function CashInEvolutionCard({ token, mainPreset }: { token: string; mainPreset: PeriodPreset }) {
  // Defaults au preset principal si compatible, sinon `next12months` (la vue
  // forward-looking étant la plus pertinente pour la trésorerie).
  const initialPreset: PeriodPreset = CASH_IN_PRESETS.some((p) => p.key === mainPreset)
    ? mainPreset
    : 'next12months';
  const [localPreset, setLocalPreset] = useState<PeriodPreset>(initialPreset);
  const [data, setData] = useState<CashInEvolutionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    kpiDirectionApi
      .cashInEvolution(token, presetPeriod(localPreset))
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erreur');
      });
    return () => {
      cancelled = true;
    };
  }, [token, localPreset]);

  // Cumulatif : à chaque mois on affiche la somme depuis le début de la
  // période sélectionnée. Visualise la trajectoire d'encaissement.
  const rows = useMemo(() => {
    let cur = 0;
    let prev = 0;
    const series = data?.series ?? [];
    // Labels désambiguïsés (année suffixée sur les buckets en collision) —
    // évite le tooltip/drill-down faux sur les fenêtres 13 mois (deux « janv. »).
    const labels = disambiguateBucketLabels(
      series.map((d) => d.month),
      'month',
    );
    return series.map((d, i) => {
      cur += d.current;
      prev += d.previousYear;
      return {
        month: labels[i],
        monthIso: d.month,
        current: Math.round(cur),
        previous: Math.round(prev),
      };
    });
  }, [data]);
  const totals = useMemo(() => {
    // Cumulé → total = dernière valeur.
    const last = rows[rows.length - 1];
    const cur = last?.current ?? 0;
    const prev = last?.previous ?? 0;
    const deltaPct = prev === 0 ? null : Math.round(((cur - prev) / prev) * 100);
    return { cur, prev, deltaPct };
  }, [rows]);
  const allEmpty = totals.cur === 0 && totals.prev === 0;

  function handleChartClick(state: unknown): void {
    const s = state as { activeLabel?: string } | null;
    const label = s?.activeLabel;
    if (!label) return;
    const row = rows.find((r) => r.month === label);
    if (!row) return;
    // Drill-down : opps `won` dont effective_date tombe dans ce mois.
    // `monthIso` est un ISO datetime COMPLET (ex. "2026-03-01T00:00:00.000Z" -
    // cf backend `getCashInEvolution` qui fait `new Date(r.month).toISOString()`).
    // On en extrait le préfixe AAAA-MM pour construire des bornes date-only
    // `YYYY-MM-DD` : c'est le format STRICT exigé par effective_after /
    // effective_before (regex `^\d{4}-\d{2}-\d{2}$` côté backend, contrairement
    // à created_after / signed_after qui acceptent un ISO datetime complet).
    // Bug corrigé 2026-06-05 : concaténer sur le monthIso complet produisait
    // "2026-03-01T00:00:00.000Z-01" → Invalid Date → 422 "Validation failed".
    const ym = row.monthIso.slice(0, 7); // "2026-03"
    const [y, m] = ym.split('-').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(m)) return;
    const lastDay = new Date(Date.UTC(y as number, m as number, 0)).getUTCDate();
    const start = `${ym}-01`;
    const end = `${ym}-${String(lastDay).padStart(2, '0')}`;
    openInPipeline({
      stage: 'won',
      effective_after: start,
      effective_before: end,
    });
  }

  return (
    <Card className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3">
        <div>
          <CardTitle>Trésorerie - encaissements à la prise d&apos;effet (cumulé)</CardTitle>
          <CardDescription>
            CA encaissé (commission + frais + honoraires) cumulé mois après mois (basé sur la date
            d&apos;effet du contrat) · comparé à N-1
          </CardDescription>
        </div>
        {/* Dropdown période au-dessus, badge de variation (Stripe-like) en dessous. */}
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Select
            value={localPreset}
            onChange={(e) => setLocalPreset(e.target.value as PeriodPreset)}
            className="w-44"
            aria-label="Période trésorerie"
          >
            {CASH_IN_PRESETS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </Select>
          {totals.deltaPct !== null && (
            <UiTooltip content="Variation vs même période N-1" side="bottom" align="end">
              <Badge
                tone={totals.deltaPct > 0 ? 'success' : totals.deltaPct < 0 ? 'danger' : 'neutral'}
              >
                {totals.deltaPct > 0 ? '+' : ''}
                {totals.deltaPct} %
              </Badge>
            </UiTooltip>
          )}
        </div>
      </div>
      <div className="mt-4 min-h-[260px] w-full flex-1">
        {error ? (
          <Banner tone="danger">{error}</Banner>
        ) : !data ? (
          <Skeleton className="h-full w-full" />
        ) : allEmpty ? (
          <EmptyState
            title="Aucun encaissement"
            description="Aucune opportunité signée n'a sa prise d'effet sur la période."
          />
        ) : (
          <ResponsiveContainer>
            <AreaChart
              data={rows}
              margin={{ left: 0, right: 8, top: 4, bottom: 0 }}
              onClick={handleChartClick}
              style={{ cursor: 'pointer' }}
            >
              <defs>
                <linearGradient id="cashInGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00B992" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#00B992" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="month" {...AXIS_STYLE} />
              <YAxis
                {...AXIS_STYLE}
                tickFormatter={(v) => formatEuroCompact(Number(v))}
                width={56}
              />
              <Tooltip
                cursor={{ stroke: '#E5E7EB', strokeWidth: 1 }}
                content={<ChartTooltip formatValue={formatEuroCompact} />}
              />
              <Legend
                verticalAlign="top"
                align="right"
                height={24}
                iconType="circle"
                wrapperStyle={{ fontSize: 11, color: '#6B7280' }}
              />
              <Area
                type="monotone"
                name="N-1"
                dataKey="previous"
                stroke="#9CA3AF"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                fill="transparent"
                dot={false}
                activeDot={{ r: 3, fill: '#9CA3AF' }}
              />
              <Area
                type="monotone"
                name="Encaissé / à encaisser"
                dataKey="current"
                stroke="#00B992"
                strokeWidth={2}
                fill="url(#cashInGradient)"
                dot={false}
                activeDot={{ r: 4, fill: '#00B992', strokeWidth: 2, stroke: '#FFFFFF' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Prévisions CA - barres groupées
// ---------------------------------------------------------------------------

function ForecastCard({ data }: { data: ForecastResponse }) {
  const accent = getAccentHex();
  const rows = data.horizons.map((h) => ({
    label: `${h.days} j`,
    days: h.days,
    CA: Math.round(h.weightedRevenue),
    opps: h.opportunityCount,
  }));
  return (
    <Card>
      <CardTitle>Prévisions CA pondéré</CardTitle>
      <CardDescription>Projection - clic pour voir le détail</CardDescription>
      <div className="mt-4 h-[260px] w-full">
        <ResponsiveContainer>
          <BarChart data={rows} margin={{ left: 0, right: 8, top: 4 }}>
            <CartesianGrid {...GRID_STYLE} />
            <XAxis dataKey="label" {...AXIS_STYLE} />
            <YAxis {...AXIS_STYLE} tickFormatter={(v) => formatEuroCompact(Number(v))} width={56} />
            <Tooltip
              cursor={{ fill: 'rgba(99, 91, 255, 0.04)' }}
              content={<ChartTooltip formatValue={formatEuroCompact} />}
            />
            <Bar dataKey="CA" name="CA pondéré" fill={accent} radius={[4, 4, 0, 0]} maxBarSize={18}>
              {rows.map((row) => (
                <Cell
                  key={row.days}
                  cursor="pointer"
                  onClick={() => {
                    const stages = FORECAST_HORIZON_STAGES[row.days];
                    if (stages) openInPipeline({ stages: stages.join(',') });
                  }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Motifs de perte - pie
// ---------------------------------------------------------------------------

function LossPieCard({
  data,
  period,
}: {
  data: LossAnalysisResponse;
  period: { period_start: string; period_end: string };
}) {
  const rows = data.byReason.map((b) => ({
    name: LOSS_REASON_LABELS[b.reason] ?? b.reason,
    value: b.count,
    reason: b.reason,
  }));
  return (
    <Card className="flex h-full flex-col">
      <CardTitle>Motifs de perte</CardTitle>
      <CardDescription>Répartition - clic pour voir le détail</CardDescription>
      <div className="mt-4 min-h-[260px] w-full flex-1">
        {rows.length === 0 ? (
          <EmptyState title="Aucune opportunité perdue" />
        ) : (
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={rows}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={72}
                outerRadius={88}
                paddingAngle={2}
                stroke="#FFFFFF"
                strokeWidth={2}
                cursor="pointer"
                onClick={(_, index) => {
                  // Recharts Pie : index est fiable, le 1er arg varie selon
                  // les versions. On retombe sur la row d'origine via index.
                  const row = rows[index];
                  if (!row) return;
                  // Backend = stage=lost AND stage_changed_at dans la période.
                  if (row.reason && row.reason !== 'unknown') {
                    openInPipeline({
                      stage: 'lost',
                      loss_reason: row.reason,
                      stage_changed_after: period.period_start,
                      stage_changed_before: period.period_end,
                    });
                  } else {
                    // Pas de filtre `loss_reason=null` côté API → on tombe sur
                    // tous les `lost` de la période. Acceptable en V1.
                    openInPipeline({
                      stage: 'lost',
                      stage_changed_after: period.period_start,
                      stage_changed_before: period.period_end,
                    });
                  }
                }}
              >
                {rows.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Motifs de perte - courbe par mois
// ---------------------------------------------------------------------------

function LossEvolutionCard({
  data,
  granularity,
}: {
  data: LossAnalysisResponse;
  granularity: Granularity;
}) {
  const rows = useMemo(() => {
    // Labels désambiguïsés (année suffixée sur les buckets en collision) —
    // évite le tooltip/drill-down faux sur les fenêtres 13 mois (deux « janv. »).
    const labels = disambiguateBucketLabels(
      data.byMonthSeries.map((d) => d.month),
      granularity,
    );
    return data.byMonthSeries.map((d, i) => ({
      month: labels[i],
      // Préservé pour le drill-down - non affiché.
      monthIso: d.month,
      current: d.count,
      previous: d.previousYearCount,
    }));
  }, [data, granularity]);

  function handleChartClick(state: unknown): void {
    // Click → opps perdues dans CE bucket (filtre stage=lost + stage_changed_at
    // dans le bucket). N-1 ignorée comme demandé.
    const s = state as { activeLabel?: string } | null;
    const label = s?.activeLabel;
    if (!label) return;
    const row = rows.find((r) => r.month === label);
    if (!row) return;
    const range = bucketToIsoRange(row.monthIso, granularity);
    if (!range) return;
    openInPipeline({
      stage: 'lost',
      stage_changed_after: range.start,
      stage_changed_before: range.end,
    });
  }
  const totals = useMemo(() => {
    const cur = rows.reduce((s, r) => s + r.current, 0);
    const prev = rows.reduce((s, r) => s + r.previous, 0);
    const deltaPct = prev === 0 ? null : Math.round(((cur - prev) / prev) * 100);
    return { cur, prev, deltaPct };
  }, [rows]);
  const allEmpty = totals.cur === 0 && totals.prev === 0;

  return (
    <Card className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3">
        <div>
          <CardTitle>Évolution des pertes</CardTitle>
          <CardDescription>
            Nombre d&apos;opportunités perdues par mois · comparé à N-1
          </CardDescription>
        </div>
        {totals.deltaPct !== null && (
          <span
            className={cn(
              'shrink-0 text-xs font-medium',
              // Inverse : plus de pertes = mauvais (rouge), moins = bon (vert)
              totals.deltaPct < 0
                ? 'text-success-600'
                : totals.deltaPct > 0
                  ? 'text-danger-600'
                  : 'text-text-muted',
            )}
          >
            {totals.deltaPct > 0 ? '+' : ''}
            {totals.deltaPct} % vs N-1
          </span>
        )}
      </div>
      <div className="mt-4 min-h-[260px] w-full flex-1">
        {allEmpty ? (
          <EmptyState title="Aucune donnée" />
        ) : (
          <ResponsiveContainer>
            <AreaChart
              data={rows}
              margin={{ left: 0, right: 8, top: 4 }}
              onClick={handleChartClick}
              style={{ cursor: 'pointer' }}
            >
              <defs>
                <linearGradient id="lossGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F87171" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#F87171" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="month" {...AXIS_STYLE} />
              <YAxis {...AXIS_STYLE} width={32} allowDecimals={false} />
              <Tooltip cursor={{ stroke: '#E5E7EB', strokeWidth: 1 }} content={<ChartTooltip />} />
              <Legend
                verticalAlign="top"
                align="right"
                height={24}
                iconType="circle"
                wrapperStyle={{ fontSize: 11, color: '#6B7280' }}
              />
              <Area
                type="monotone"
                name="N-1"
                dataKey="previous"
                stroke="#9CA3AF"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                fill="transparent"
                dot={false}
                activeDot={{ r: 3, fill: '#9CA3AF' }}
              />
              <Area
                type="monotone"
                name="Cette période"
                dataKey="current"
                stroke="#F87171"
                strokeWidth={2}
                fill="url(#lossGradient)"
                dot={false}
                activeDot={{ r: 4, fill: '#F87171', strokeWidth: 2, stroke: '#FFFFFF' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Conversion compagnies / types de contrat - barres horizontales
// ---------------------------------------------------------------------------

function CompanyConversionCard({
  data,
  period,
}: {
  data: CompanyConversionResponse;
  period: { period_start: string; period_end: string };
}) {
  // Refonte 2026-05-22 : passage du BarChart au tableau dense pour intégrer
  // les nouvelles colonnes inquiries (interrogations + refus + taux). Plus
  // lisible que de superposer 2 BarCharts. Tri DESC sur la conversion %
  // par défaut (compagnies les plus performantes en tête).
  const rows = [...data.rows].sort((a, b) => b.conversionRatePct - a.conversionRatePct);

  return (
    <Card className="h-full">
      <CardTitle>Conversion par compagnie</CardTitle>
      <CardDescription>
        Devis + interrogations sur la période - clic pour voir le détail
      </CardDescription>
      {rows.length === 0 ? (
        <div className="mt-4 h-[120px]">
          <EmptyState title="Aucune activité sur la période" />
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto overflow-y-auto" style={{ maxHeight: 360 }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-100 text-[11px] uppercase tracking-wider text-text-muted">
                <th className="py-2 pr-2 text-left font-medium">Compagnie</th>
                <th
                  className="py-2 px-2 text-right font-medium"
                  title="Devis émis sur la période (= PDF uploadés)"
                >
                  Devis
                </th>
                <th className="py-2 px-2 text-right font-medium" title="Ratio devis signés / total">
                  Conv.
                </th>
                <th
                  className="py-2 px-2 text-right font-medium"
                  title="Compagnies interrogées sur la période (sollicitations)"
                >
                  Interr.
                </th>
                <th
                  className="py-2 pl-2 text-right font-medium"
                  title="% inquiries refusées par cette compagnie"
                >
                  Refus
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr
                  key={r.companyId}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() =>
                    openInPipeline({
                      company_id: r.companyId,
                      created_after: period.period_start,
                      created_before: period.period_end,
                    })
                  }
                >
                  <td className="py-2 pr-2 font-medium text-brand">{r.name}</td>
                  <td className="py-2 px-2 text-right tabular-nums text-text-muted">
                    {r.quotesTotal}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums">
                    {r.quotesTotal === 0 ? (
                      <span className="text-text-muted">—</span>
                    ) : (
                      <span
                        className={
                          r.conversionRatePct >= 30
                            ? 'font-medium text-success-600'
                            : r.conversionRatePct >= 10
                              ? 'text-warning-600'
                              : 'text-text-muted'
                        }
                      >
                        {r.conversionRatePct}%
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums text-text-muted">
                    {r.inquiriesTotal}
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums">
                    {r.inquiriesTotal === 0 ? (
                      <span className="text-text-muted">—</span>
                    ) : (
                      <span
                        className={
                          r.refusalRatePct >= 50
                            ? 'font-medium text-danger-600'
                            : r.refusalRatePct >= 25
                              ? 'text-warning-600'
                              : 'text-text-muted'
                        }
                      >
                        {r.refusalRatePct}%
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/**
 * Stats globales inquiries (demande LBC 2026-05-22) — mini-card avec
 * 3 grands chiffres : moyenne compagnies interrogées par opp + taux de
 * transformation (interrogé → devis reçu) + taux de refus.
 */
function InquiriesStatsCard({ data }: { data: InquiriesStatsResponse }) {
  return (
    <Card className="h-full">
      <CardTitle>Activité de sollicitation</CardTitle>
      <CardDescription>
        {data.inquiriesTotal} interrogations sur {data.oppsWithInquiries} opportunité
        {data.oppsWithInquiries === 1 ? '' : 's'}
      </CardDescription>
      <div className="mt-4 grid grid-cols-3 gap-4">
        <div>
          <div className="text-2xl font-semibold text-brand tabular-nums">
            {data.avgInquiriesPerOpp.toLocaleString('fr-FR')}
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-wider text-text-muted">
            Compagnies / opp
          </div>
          <div className="mt-0.5 text-xs text-text-muted">en moyenne</div>
        </div>
        <div>
          <div
            className={cn(
              'text-2xl font-semibold tabular-nums',
              data.transformationRatePct >= 60
                ? 'text-success-600'
                : data.transformationRatePct >= 30
                  ? 'text-warning-600'
                  : 'text-brand',
            )}
          >
            {data.transformationRatePct}%
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-wider text-text-muted">
            Devis reçus
          </div>
          <div className="mt-0.5 text-xs text-text-muted">sur compagnies interrogées</div>
        </div>
        <div>
          <div
            className={cn(
              'text-2xl font-semibold tabular-nums',
              data.refusalRatePct >= 50
                ? 'text-danger-600'
                : data.refusalRatePct >= 25
                  ? 'text-warning-600'
                  : 'text-brand',
            )}
          >
            {data.refusalRatePct}%
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-wider text-text-muted">Refus</div>
          <div className="mt-0.5 text-xs text-text-muted">d&apos;émission de devis</div>
        </div>
      </div>
    </Card>
  );
}

function TypeConversionCard({
  data,
  period,
}: {
  data: ContractTypeConversionResponse;
  period: { period_start: string; period_end: string };
}) {
  const accent = getAccentHex();
  // Filtre défensif : on n'affiche QUE les types ayant au moins une
  // opportunité créée sur la période. Le backend exclut déjà les types
  // sans opp via INNER JOIN, mais on double-protège ici au cas où la
  // requête évoluerait.
  // Tri DESC : le type le mieux converti en premier.
  const rows = data.rows
    .filter((r) => r.opportunitiesTotal > 0)
    .map((r) => ({
      name: r.code,
      taux: r.conversionRatePct,
      contractTypeId: r.contractTypeId,
    }))
    .sort((a, b) => b.taux - a.taux);
  // Hauteur : 8 lignes max visibles, scroll vertical au-delà. Cf CompanyConversionCard.
  const ROW_H = 32;
  const AXIS_H = 40;
  const MAX_VISIBLE = 8;
  const innerHeight = Math.max(rows.length, 1) * ROW_H + AXIS_H;
  const outerMaxHeight = Math.min(rows.length, MAX_VISIBLE) * ROW_H + AXIS_H;
  return (
    <Card>
      <CardTitle>Conversion par type de contrat</CardTitle>
      <CardDescription>Signature par type - clic pour voir le détail</CardDescription>
      <div className="mt-4 w-full overflow-y-auto" style={{ maxHeight: outerMaxHeight }}>
        {rows.length === 0 ? (
          <div className="h-[120px]">
            <EmptyState title="Aucune donnée" />
          </div>
        ) : (
          <div style={{ height: innerHeight }}>
            <ResponsiveContainer>
              <BarChart data={rows} layout="vertical" margin={{ left: 0, right: 16, top: 4 }}>
                <CartesianGrid {...GRID_STYLE} horizontal={false} vertical />
                <XAxis
                  type="number"
                  orientation="top"
                  {...AXIS_STYLE}
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                />
                <YAxis type="category" dataKey="name" {...AXIS_STYLE} width={80} interval={0} />
                <Tooltip
                  cursor={{ fill: 'rgba(99, 91, 255, 0.04)' }}
                  content={<ChartTooltip formatValue={(v) => `${v} %`} />}
                />
                <Bar
                  dataKey="taux"
                  name="Taux conversion"
                  fill={accent}
                  radius={[0, 999, 999, 0]}
                  maxBarSize={14}
                >
                  {rows.map((row) => (
                    <Cell
                      key={row.contractTypeId}
                      cursor="pointer"
                      // Backend = opps créées dans la période, groupées par type.
                      onClick={() =>
                        openInPipeline({
                          contract_type_id: row.contractTypeId,
                          created_after: period.period_start,
                          created_before: period.period_end,
                        })
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Top 10 apporteurs - card mini-table sur la même row que les graphes
// conversion compagnie / type contrat. Tri DESC par CA généré (brut),
// affiche aussi les rétrocoms et le taux de conversion (cohorte). Cf
// SECURITY.md session 2026-04-27.
// ---------------------------------------------------------------------------

function TopApporteursCard({ data }: { data: TopApporteursResponse | null }) {
  // `h-full` : la card s'étire à la hauteur du sibling Trésorerie sur la
  // même grid-row (alignement visuel demandé par le client). La table
  // interne est en `overflow-x-auto` donc le contenu reste lisible si
  // la card grossit verticalement.
  if (!data) {
    return (
      <Card className="h-full">
        <CardTitle>Top apporteurs</CardTitle>
        <CardDescription>Clic pour voir le détail</CardDescription>
        <div className="mt-4 h-[120px]">
          <EmptyState title="Chargement…" />
        </div>
      </Card>
    );
  }
  return (
    <Card className="h-full">
      <CardTitle>Top apporteurs</CardTitle>
      <CardDescription>Clic pour voir le détail</CardDescription>
      {data.rows.length === 0 ? (
        <div className="mt-4 h-[120px]">
          <EmptyState
            title="Aucune activité"
            description="Aucun apporteur n'a apporté ou signé sur cette période."
          />
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-[11px] uppercase tracking-wider text-text-muted">
                <th className="py-2 pr-2 text-left font-medium">Apporteur</th>
                <th className="py-2 px-2 text-right font-medium">CA</th>
                <th className="py-2 px-2 text-right font-medium">Rétro</th>
                <th className="py-2 pl-2 text-right font-medium">Conv.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.map((r) => (
                <tr
                  key={r.apporteurId}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() =>
                    openInPipeline({
                      apporteur_id: r.apporteurId,
                      created_after: data.period.start,
                      created_before: data.period.end,
                    })
                  }
                >
                  <td className="py-2 pr-2">
                    <div className="font-medium text-brand">
                      {apporteurDisplayName({
                        type: r.type,
                        firstName: r.firstName,
                        lastName: r.lastName,
                        companyName: r.companyName,
                      })}
                      {!r.isActive && (
                        <span className="ml-1.5 text-[10px] text-text-muted">(désactivé)</span>
                      )}
                    </div>
                    {r.type === 'person' && r.company && (
                      <div className="text-[11px] text-text-muted">{r.company}</div>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums text-brand">
                    {Math.round(r.caGenere).toLocaleString('fr-FR')} €
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums text-text-muted">
                    {Math.round(r.retrocoms).toLocaleString('fr-FR')} €
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums text-text-muted">
                    {r.conversionRatePct === null ? '—' : `${r.conversionRatePct}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Répartition par origine — donut + tableau breakdown (drill-down par ligne)
// Demande client 2026-05-22. Cf endpoint /api/v1/kpi/revenue-by-origin.
// ---------------------------------------------------------------------------

// Palette dédiée aux origines, alignée avec `ORIGIN_VISUAL` côté UI
// (libellés badge). Les couleurs hex utilisées doivent matcher la
// nuance visible des badges pour cohérence visuelle.
const ORIGIN_CHART_COLORS: Record<OpportunityOrigin | 'none', string> = {
  campaign: '#3E63DD', // accent
  prospecting: '#2563EB', // blue-600
  recommendation: '#059669', // success-600
  saturation: '#7C3AED', // violet-600
  inbound_call: '#D97706', // warning-600
  linkedin: '#4F46E5', // indigo-600
  trade_show: '#B45309', // amber-700
  // Ajout 2026-05-28 — alignées sur les badges ORIGIN_VISUAL.
  google: '#DC2626', // red-600
  kompass: '#0D9488', // teal-600
  apporteur: '#059669', // emerald-600
  direct_canvassing: '#0284C7', // sky-600
  national_registry: '#475569', // slate-600
  yellow_pages: '#CA8A04', // yellow-600
  tv_media: '#C026D3', // fuchsia-600
  other: '#6B7280', // gray-500
  none: '#9CA3AF', // gray-400 (non renseignée)
};

function originLabel(code: OpportunityOrigin | null): string {
  return code === null ? 'Non renseignée' : ORIGIN_VISUAL[code].label;
}

function RevenueByOriginDonut({ data }: { data: RevenueByOriginResponse }) {
  // On trie par CA pondéré décroissant (NB. les rows backend ne sont pas
  // triées). Les origines à 0 reste affichées si présentes, le donut les
  // ignore visuellement (segment de 0°).
  const rows = useMemo(() => {
    return [...data.rows]
      .sort((a, b) => b.weightedRevenue - a.weightedRevenue)
      .map((r) => ({
        key: (r.origin ?? 'none') as OpportunityOrigin | 'none',
        name: originLabel(r.origin),
        value: r.weightedRevenue,
        count: r.oppCount,
      }));
  }, [data.rows]);

  if (data.total.oppCount === 0) {
    return (
      <Card className="flex h-full flex-col">
        <CardTitle>Répartition par origine</CardTitle>
        <CardDescription>Canal d&apos;acquisition - opps créées</CardDescription>
        <div className="mt-4 flex-1">
          <EmptyState
            title="Aucune opportunité"
            description="Aucune opp créée sur cette période."
          />
        </div>
      </Card>
    );
  }

  return (
    <Card className="flex h-full flex-col">
      <CardTitle>Répartition par origine</CardTitle>
      <CardDescription>CA pondéré par canal d&apos;acquisition</CardDescription>
      <div className="mt-4 min-h-[260px] w-full flex-1">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={rows}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={72}
              outerRadius={88}
              paddingAngle={2}
              stroke="#FFFFFF"
              strokeWidth={2}
            >
              {rows.map((r) => (
                <Cell key={r.key} fill={ORIGIN_CHART_COLORS[r.key]} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip formatValue={formatEuroCompact} />} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function RevenueByOriginTable({
  data,
  period,
}: {
  data: RevenueByOriginResponse;
  period: { period_start: string; period_end: string };
}) {
  // Tri par CA pondéré décroissant — cohérent avec le donut. Les origines
  // à 0 restent affichées (transparence pour le commercial : « personne
  // depuis Linkedin sur la période »).
  const rows = useMemo(() => {
    return [...data.rows].sort((a, b) => b.weightedRevenue - a.weightedRevenue);
  }, [data.rows]);

  return (
    <Card className="h-full">
      <CardTitle>Détail par origine</CardTitle>
      <CardDescription>Clic sur une ligne pour drill-down</CardDescription>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-[11px] uppercase tracking-wider text-text-muted">
              <th className="py-2 pr-2 text-left font-medium">Canal</th>
              <th className="py-2 px-2 text-right font-medium">Opps créées</th>
              <th className="py-2 px-2 text-right font-medium">Signées</th>
              <th className="py-2 pl-2 text-right font-medium">CA pondéré</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => {
              const key = r.origin ?? 'none';
              return (
                <tr
                  key={key}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() =>
                    openInPipeline({
                      origins: key,
                      created_after: period.period_start,
                      created_before: period.period_end,
                    })
                  }
                >
                  <td className="py-2 pr-2">
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: ORIGIN_CHART_COLORS[key] }}
                      />
                      <span className="font-medium text-brand">{originLabel(r.origin)}</span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums text-text-muted">
                    {r.oppCount}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums text-text-muted">
                    {r.signedCount}
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums text-brand">
                    {Math.round(r.weightedRevenue).toLocaleString('fr-FR')} €
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Classement commerciaux
// ---------------------------------------------------------------------------

function LeaderboardCard({ data }: { data: UserPerformanceResponse }) {
  const cols: Column<UserPerformanceRow>[] = [
    {
      key: 'name',
      header: 'Commercial',
      render: (r) => (
        <span className="font-medium text-brand">
          {r.firstName} {r.lastName}
        </span>
      ),
    },
    {
      key: 'rev',
      header: 'CA signé',
      align: 'right',
      render: (r) => `${Math.round(r.revenueSigned).toLocaleString('fr-FR')} €`,
    },
    { key: 'signed', header: 'Signées', align: 'right', render: (r) => r.signedCount.toString() },
    { key: 'lost', header: 'Perdues', align: 'right', render: (r) => r.lostCount.toString() },
    {
      key: 'conv',
      header: 'Conv.',
      align: 'right',
      render: (r) => (
        <span
          className={cn(
            r.conversionRatePct >= 30 && 'text-success-600',
            r.conversionRatePct < 15 && r.createdCount > 0 && 'text-danger-600',
          )}
        >
          {r.conversionRatePct} %
        </span>
      ),
    },
    { key: 'calls', header: 'Appels', align: 'right', render: (r) => r.callsCount.toString() },
    { key: 'meet', header: 'RDV', align: 'right', render: (r) => r.meetingsCount.toString() },
    { key: 'quotes', header: 'Devis', align: 'right', render: (r) => r.quotesCount.toString() },
  ];

  return (
    <Card>
      <CardTitle>Classement commerciaux</CardTitle>
      <CardDescription>
        CA signé et activité sur la période. {data.rows.length} commercial
        {data.rows.length > 1 ? 's' : ''}.
      </CardDescription>
      <div className="mt-3">
        <DataTable
          columns={cols}
          data={data.rows}
          keyExtractor={(r) => r.userId}
          empty={{ title: 'Aucun commercial actif sur la période' }}
        />
      </div>
    </Card>
  );
}
